import { IBaseParams } from '@interfaces/IBaseParam';
import { callAPIContent } from '@utils/fetcher/content';
import {
  IParamCreateFinancialReportRecap,
  IParamImportFinancialReportRecap,
  IParamFinancialReportProductBulkBySku,
  IParamFinancialReportProductBulkUpdate,
  IParamFinancialReportProductUpdate,
  IParamFinancialReportRecapAction,
  IParamFinancialReportSpending,
  IParamFinancialReportTab,
  IParamUploadFinancialReportRecapFiles,
} from './financial-report.types';

export const getFinancialReportRecapPageFromAPI = (params: IBaseParams) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/page',
    params,
  };
  return callAPIContent(options);
};

export const createFinancialReportRecapFromAPI = (
  params: IParamCreateFinancialReportRecap,
) => {
  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: 'recaps',
    params,
  };
  return callAPIContent(options);
};

export const importFinancialReportRecapFromAPI = (
  params: IParamImportFinancialReportRecap,
) => {
  const formData = new FormData();

  formData.append('file', params.file);
  if (params.report_name) {
    formData.append('report_name', params.report_name);
  }
  if (params.integration_id) {
    formData.append('integration_id', String(params.integration_id));
  }
  formData.append('channel_id', String(params.channel_id || ''));
  if (params.warehouse_id !== undefined) {
    formData.append('warehouse_id', String(params.warehouse_id));
  }
  if (params.period_start) {
    formData.append('period_start', params.period_start);
  }
  if (params.period_end) {
    formData.append('period_end', params.period_end);
  }
  if (params.date_type) {
    formData.append('date_type', params.date_type);
  }

  if (params.client_id) {
    formData.append('client_id', String(params.client_id));
  }

  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: 'recaps/import',
    params: formData,
    additionalHeader: {
      'Content-Type': 'multipart/form-data',
    },
  };
  return callAPIContent(options);
};

export const uploadFinancialReportRecapFilesFromAPI = (
  params: IParamUploadFinancialReportRecapFiles,
) => {
  const formData = new FormData();

  formData.append('recap_order_id', String(params.recap_order_id));

  if (params.file_pesanan) {
    formData.append('file_pesanan', params.file_pesanan);
  }

  if (params.file_pendapatan) {
    formData.append('file_pendapatan', params.file_pendapatan);
  }

  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: 'recaps/upload',
    params: formData,
    additionalHeader: {
      'Content-Type': 'multipart/form-data',
    },
  };
  return callAPIContent(options);
};

export const uploadFinancialReportRecapPreviewFromAPI = (
  params: IParamUploadFinancialReportRecapFiles,
) => {
  const formData = new FormData();

  formData.append('recap_order_id', String(params.recap_order_id));

  if (params.file_pesanan) {
    formData.append('file_pesanan', params.file_pesanan);
  }

  if (params.file_pendapatan) {
    formData.append('file_pendapatan', params.file_pendapatan);
  }

  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: 'recaps/upload-preview',
    params: formData,
    additionalHeader: {
      'Content-Type': 'multipart/form-data',
    },
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapPreviewResultFromAPI = (
  previewId: string,
) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/upload-preview',
    params: { preview_id: previewId },
  };
  return callAPIContent(options);
};

export const cancelFinancialReportRecapPreviewFromAPI = (previewId: string) => {
  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: `recaps/upload-preview/cancel?preview_id=${encodeURIComponent(
      previewId,
    )}`,
  };
  return callAPIContent(options);
};

// generate recap dari file yang disimpan server saat preview (tak perlu upload ulang)
export const confirmFinancialReportRecapPreviewFromAPI = (
  previewId: string,
) => {
  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: `recaps/upload-preview/confirm?preview_id=${encodeURIComponent(
      previewId,
    )}`,
  };
  return callAPIContent(options);
};

export const regenerateFinancialReportRecapFromAPI = (
  params: IParamFinancialReportRecapAction,
) => {
  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: 'recaps/regenerate',
    params,
  };
  return callAPIContent(options);
};

export const deleteFinancialReportRecapFromAPI = (id: number) => {
  const options = {
    method: 'DELETE',
    servicePath: 'financial-reports',
    uri: 'recaps',
    params: { id },
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapDetailFromAPI = (id: number) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/detail',
    params: { id },
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapAnalyticsFromAPI = (id: number) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/analytics',
    params: { id },
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapProfitLossFromAPI = (id: number) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/profit-loss',
    params: { id },
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapCashflowFromAPI = (id: number) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/cashflow',
    params: { id },
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapImportTasksFromAPI = (
  params: IParamFinancialReportTab,
) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/import-tasks',
    params,
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapOrdersFromAPI = (
  params: IParamFinancialReportTab,
) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/orders',
    params,
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapProductsFromAPI = (
  params: IParamFinancialReportTab,
) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/products',
    params,
  };
  return callAPIContent(options);
};

export const updateFinancialReportProductsBulkFromAPI = (
  params: IParamFinancialReportProductBulkUpdate,
) => {
  const options = {
    method: 'PUT',
    servicePath: 'financial-reports',
    uri: 'recaps/products/bulk',
    params,
  };
  return callAPIContent(options);
};

export const updateFinancialReportProductFromAPI = (
  params: IParamFinancialReportProductUpdate,
) => {
  const options = {
    method: 'PUT',
    servicePath: 'financial-reports',
    uri: 'recaps/products',
    params,
  };
  return callAPIContent(options);
};

export const updateFinancialReportProductsBulkBySkuFromAPI = (
  params: IParamFinancialReportProductBulkBySku,
) => {
  const options = {
    method: 'PUT',
    servicePath: 'financial-reports',
    uri: 'recaps/products/bulk-by-sku',
    params,
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapInventoryFromAPI = (
  params: IParamFinancialReportTab,
) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/products/inventory',
    params,
  };
  return callAPIContent(options);
};

export const getFinancialReportRecapSpendingFromAPI = (
  params: IParamFinancialReportTab,
) => {
  const options = {
    method: 'GET',
    servicePath: 'financial-reports',
    uri: 'recaps/spending',
    params,
  };
  return callAPIContent(options);
};

export const createFinancialReportSpendingFromAPI = (
  params: IParamFinancialReportSpending,
) => {
  const options = {
    method: 'POST',
    servicePath: 'financial-reports',
    uri: 'recaps/spending',
    params,
  };
  return callAPIContent(options);
};

export const updateFinancialReportSpendingFromAPI = (
  params: IParamFinancialReportSpending,
) => {
  const options = {
    method: 'PUT',
    servicePath: 'financial-reports',
    uri: 'recaps/spending',
    params,
  };
  return callAPIContent(options);
};

export const deleteFinancialReportSpendingFromAPI = (id: number) => {
  const options = {
    method: 'DELETE',
    servicePath: 'financial-reports',
    uri: 'recaps/spending',
    params: { id },
  };
  return callAPIContent(options);
};
